import { useEffect, useState } from 'react'
import { Activity, ChevronDown, ChevronUp } from 'lucide-react'
import { ApiError } from '../lib/api'
import { useLang, t } from '../lib/i18n'
import { SAMPLE_DEVICES, type DeviceMriStatus, type DeviceView } from '../lib/samples/devices'

/**
 * DevicesPanel — implanted devices & external clinical hardware.
 *
 * Port of the prototype's Devices section (PHRPrototype_5.jsx, DEVICES_SY /
 * SY_PHR_Clinical_Device_Master_v0_1.xlsx): high-visibility implanted devices
 * matter because they may affect MRI, surgery, electrocautery, emergency
 * care, or medication/procedure decisions.
 *
 * Backend: no device model/router exists yet. API-READY pattern (same as
 * VitalsPanel): attempt the future contract
 *
 *   GET /api/v1/patients/{id}/devices → { items, total }
 *
 * first, and on any failure render the bundled samples from
 * lib/samples/devices.ts with the existing "Sample data" badge
 * (prov.sampleData). Rows show EN/AR device type, implant date, status, and
 * an MRI-safety chip; a red alert dot marks implanted, high-visibility
 * devices (prototype HighVisibilityPHR flag). UDI-DI / serial / site detail
 * expands per row.
 */

type PanelState =
  | { phase: 'loading' }
  | { phase: 'ready'; items: DeviceView[]; sampled: boolean }

/** Placeholder client for the future devices endpoint. */
async function listPatientDevices(
  patientId: string,
  options: { signal?: AbortSignal } = {},
): Promise<{ items: DeviceView[]; total: number }> {
  const API_BASE =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
    (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : 'http://127.0.0.1:8000/api/v1')
  const res = await fetch(`${API_BASE}/patients/${patientId}/devices`, { signal: options.signal })
  if (!res.ok) {
    let detail: unknown = res.statusText
    try {
      detail = (await res.json()).detail ?? res.statusText
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail)
  }
  return (await res.json()) as { items: DeviceView[]; total: number }
}

function dateFmt(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length > 10 ? iso.slice(0, 10) : iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** MRI-safety chip classes per state: emerald / amber / red / slate. */
function mriChip(mri: DeviceMriStatus): { cls: string; key: string } {
  switch (mri) {
    case 'safe':
      return { cls: 'border-emerald-200 bg-emerald-50 text-emerald-800', key: 'dev.mri.safe' }
    case 'conditional':
      return { cls: 'border-amber-300 bg-amber-50 text-amber-800', key: 'dev.mri.conditional' }
    case 'unsafe':
      return { cls: 'border-red-200 bg-red-50 text-red-800', key: 'dev.mri.unsafe' }
    default:
      return { cls: 'border-stone-200 bg-stone-100 text-stone-600', key: 'dev.mri.unknown' }
  }
}

function statusLabel(status: DeviceView['status']): { cls: string; key: string } {
  switch (status) {
    case 'active':
      return { cls: 'border-emerald-200 bg-emerald-50 text-emerald-800', key: 'dev.status.active' }
    case 'removed':
      return { cls: 'border-stone-200 bg-stone-100 text-stone-600', key: 'dev.status.removed' }
    case 'replaced':
      return { cls: 'border-sky-200 bg-sky-50 text-sky-800', key: 'dev.status.replaced' }
    default:
      return { cls: 'border-stone-200 bg-stone-100 text-stone-600', key: 'dev.status.inactive' }
  }
}

function DeviceRow({ device }: { device: DeviceView }) {
  const [open, setOpen] = useState(false)
  const mri = mriChip(device.mri_status)
  const alert = device.implanted && device.high_visibility
  const removed = device.status === 'removed' || device.status === 'replaced'

  return (
    <li className="py-3" data-testid="devices-row">
      <div className="flex items-start gap-3">
        {alert && (
          <span
            className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-500"
            aria-label={t('dev.implantedAria')}
            title={t('dev.implantedAria')}
            data-testid="devices-alert-dot"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2" dir="auto">
            {/* Bilingual master-list name; data itself may be EN-only. */}
            <span className={`text-base font-semibold ${removed ? 'text-stone-500' : 'text-stone-900'}`}>
              <span lang="en">{device.device_type_en}</span>
              <span className="mx-1.5 text-stone-300" aria-hidden="true">
                ·
              </span>
              <span lang="ar" dir="rtl" className="font-medium">
                {device.device_type_ar}
              </span>
            </span>
            <span
              className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${mri.cls}`}
              data-testid={`devices-mri-${device.mri_status}`}
            >
              {t(mri.key)}
            </span>
            {device.status !== 'active' && (
              <span
                className="inline-flex items-center rounded-md border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-[10px] font-semibold text-stone-600"
                data-testid="devices-status"
              >
                {t(statusLabel(device.status).key)}
              </span>
            )}
          </div>
          <span className="mt-0.5 block text-xs text-stone-500" dir="auto">
            {t('dev.implantedOn')} {dateFmt(device.implant_date)}
            {device.removal_replacement_date && ` · ${t('dev.removedOn')} ${dateFmt(device.removal_replacement_date)}`}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-label={t('dev.detailsAria')}
          data-testid="devices-detail-toggle"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-stone-200 text-stone-500 transition hover:bg-stone-50 active:scale-90"
        >
          {open ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>

      {open && (
        <dl
          className="mt-2 grid gap-x-6 gap-y-1 rounded-lg border border-stone-200 bg-stone-50/70 px-3 py-2 text-xs sm:grid-cols-2"
          data-testid="devices-detail"
          dir="auto"
        >
          {(
            [
              ['dev.field.udi', device.udi_di],
              ['dev.field.serial', device.serial_lot],
              ['dev.field.model', device.device_name_model],
              ['dev.field.manufacturer', device.manufacturer],
              ['dev.field.site', device.body_site],
              ['dev.field.indication', device.indication],
              ['dev.field.notes', device.notes],
            ] as const
          ).map(([key, value]) =>
            value ? (
              <div key={key} className="min-w-0">
                <dt className="font-semibold text-stone-600">{t(key)}</dt>
                <dd className="break-words text-stone-800" data-testid={`devices-${key.split('.')[2]}`}>
                  {value}
                </dd>
              </div>
            ) : null,
          )}
        </dl>
      )}
    </li>
  )
}

export function DevicesPanel({ patientId }: { patientId: string }) {
  useLang()
  const [state, setState] = useState<PanelState>({ phase: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    setState({ phase: 'loading' })
    listPatientDevices(patientId, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return
        const items = [...(res.items ?? [])].sort(
          (a, b) => new Date(b.implant_date ?? 0).getTime() - new Date(a.implant_date ?? 0).getTime(),
        )
        setState(
          items.length === 0
            ? { phase: 'ready', items: SAMPLE_DEVICES, sampled: true }
            : { phase: 'ready', items, sampled: false },
        )
      })
      .catch(() => {
        if (controller.signal.aborted) return
        // No endpoint yet / unreachable: demo fallback with "Sample data" badge.
        setState({ phase: 'ready', items: SAMPLE_DEVICES, sampled: true })
      })
    return () => controller.abort()
  }, [patientId])

  const sampled = state.phase === 'ready' && state.sampled
  const items = state.phase === 'ready' ? state.items : []
  const anyAlert = items.some((d) => d.implanted && d.high_visibility)

  return (
    <section
      className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft"
      aria-label={t('dev.aria')}
      data-testid="devices-section"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary-700" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-stone-950">{t('dev.title')}</h2>
          {/* Prototype: red dot flags any implanted, high-visibility device. */}
          {anyAlert && <span className="h-2.5 w-2.5 rounded-full bg-red-500" aria-label={t('dev.implantedAria')} data-testid="devices-header-alert" />}
        </div>
        {sampled && (
          <span
            className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
            data-testid="sample-data-badge"
          >
            {t('prov.sampleData')}
          </span>
        )}
      </div>

      {state.phase === 'loading' && (
        <div className="space-y-3" role="status" aria-label={t('dev.loading')}>
          <div className="skeleton h-10" />
          <div className="skeleton h-10" />
        </div>
      )}

      {state.phase === 'ready' && items.length === 0 && (
        <p className="px-1 py-6 text-center text-sm text-stone-500">{t('dev.empty')}</p>
      )}

      {state.phase === 'ready' && items.length > 0 && (
        <ul className="divide-y divide-stone-100" data-testid="devices-list">
          {items.map((device) => (
            <DeviceRow key={device.id} device={device} />
          ))}
        </ul>
      )}
    </section>
  )
}